import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import config from "../../config.js";
import RoutingStrip from "../../components/RoutingStrip.jsx";
import PdfButton from "../ui/PdfButton.jsx";
import { phoneUi } from "../ui/phoneUi";
import PalletCard from "./salesOrders/PalletCard.jsx";
import usePalletChecks from "../../features/palletCheck/usePalletChecks";
import { openProblemsFor } from "../../features/palletCheck/palletCheckData";
import PodSheet from "./salesOrders/PodSheet.jsx";
import OrderEditPanel from "./salesOrders/OrderEditPanel.jsx";
import { jobRouting } from "./routing";
import { orderScope, partOwnersElsewhere } from "../../utils/groupJobTasks";
import { fmtDay, fmtShipDay } from "./salesOrders/format";
import {
  batchManifestPath,
  invoicePdfPath,
  loadOrderExtras,
  loadOrderPlan,
  loadOrderTruth,
  orderPdfPath,
  shipmentBatches,
  shippedTotals,
} from "../../features/salesOrders/soData";
import {
  confirmPickup,
  confirmPickupAll,
  printLabelsAtLaser,
  queueStatusLabel,
  reprintBoxLabel,
  reprintPalletLabels,
  sealBox,
  sealPallet,
  shipPallet,
  stagePallet,
  STATUS_LABELS,
} from "../../features/salesOrders/palletActions";
import { podEligiblePallets } from "../../features/salesOrders/palletRules";
import LoadTruckModal from "../../features/shippingHub/LoadTruckModal";
import { useShippingSigningFlags } from "../../features/shippingHub/signingFlags";
import {
  deliveryLoadsOn,
  deliveryWaitingPallets,
  loadTruckOrderFromPlan,
  startDeliveryQr,
} from "../../features/shippingHub/deliverySigning";
import { absoluteUrl } from "../../features/signing/signRequestsApi";
import { useLineSteps } from "../../features/salesOrders/useLineSteps";
import { loadPod, POD_KIND_LABEL } from "../../features/salesOrders/pod";
import { accruedFinancials, flattenTruthEntries, summaryBases } from "../../utils/salesOrderMath";
import { isTm } from "../../utils/tmPricing";
import "../mobile.css";

// One sales order on a phone (plan Phase 2): header with the quick edits (PO, promised and target
// dates), delivery progress, the lines (their In Prod / Shipped as /truth splits a shared part across
// its lines: each line's own share; each line's Process Steps chips from useLineSteps, the
// desktop Line Items column's hook, a tap shows what the desktop's hover does), production by part,
// the money summary, the pallets
// (seal, ship, stage, confirm pickup with the PIN; labels go to the Laser Zebra), proof-of-delivery
// photos from the camera, and the order's PDFs. A delivery order with GET /features shipping_signing.delivery
// on ships through the shared Load truck (driver, truck, checklist, the signing QR; the manifest is offered
// here, a phone opens no tab) and gets "Delivery signing QR" for shipped pallets still waiting for a signature
// (features/shippingHub/deliverySigning, the desktop package guide's same two actions; 2026-10-28). Editing lines, packing plans, T&M pricing, invoicing
// and emails stay on the desktop page. Layout only: features/salesOrders/ is the desktop page's code.

const API_BASE = config.API_BASE_URL;
const FULFILL = { pickup: "Customer pickup", delivery: "We deliver", shipping: "Carrier shipment" };
const num = (v) => (Number.isFinite(+v) ? +v : 0);
const money = (v) => (v == null || !Number.isFinite(+v) ? "—" : `$${(+v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null || !Number.isFinite(+v) ? "—" : `${(+v).toFixed(1)}%`);

function EditButton({ label, onClick }) {
  return (
    <button type="button" className="btn btn-link btn-sm p-0 ms-2" aria-label={`Edit ${label}`} onClick={onClick}>
      <i className="fas fa-pen" aria-hidden="true" />
    </button>
  );
}

/**
 * One line: its In Prod / Shipped are its share of the part's (/truth splits it across the lines), then its
 * Process Steps (`steps` = useLineSteps' entry for the line: green all ordered, yellow some, gray
 * none, blue truck at the vendor). A phone has no hover: tapping a step shows the chip's detail
 * (jobs behind the count, manifests) under the strip; tapping it again hides it.
 */
function LineRow({ line: l, steps }) {
  const [openKey, setOpenKey] = useState(null);
  const id = l.sales_order_line_id;
  const ordered = num(l.quantity_ordered);
  const inProd = num(l.qty_in_production);
  const shipped = num(l.qty_shipped);
  const toMake = Math.max(0, ordered - shipped - inProd);
  const name = l.product_id || l.consumable_product_id || "Item";
  const open = steps && openKey ? steps.steps.find((s) => s.key === openKey) : null;
  return (
    <div className="mb-1" data-testid={`so-line-${id}`}>
      <div className="fw-semibold">{name}</div>
      {l.description && <div className="jq-muted">{l.description}</div>}
      <div>
        Ordered {ordered}{l.consumable_id ? "" : ` · In prod ${inProd} · Shipped ${shipped}`}
        {!l.consumable_id && toMake > 0 && <span className="jq-tag jq-tag-warn ms-2">{toMake} not started</span>}
      </div>
      {!l.consumable_id && steps && steps.steps.length > 0 && (
        <div className="mt-2">
          <RoutingStrip
            compact
            steps={steps.steps}
            ariaLabel={`Process steps for ${name}`}
            onStepClick={(s) => setOpenKey((k) => (k === s.key ? null : s.key))}
          />
          {steps.fromRouting && <div className="jq-muted mt-1">No job yet: from the part's routing</div>}
          {open && open.title && (
            <div className="jq-note jq-note-info mt-2 mb-0" style={{ whiteSpace: "pre-line" }} data-testid={`so-line-step-${id}`}>
              {open.title}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SalesOrderDetailScreen({ soId, onAll }) {
  const [detail, setDetail] = useState(null);
  const [extras, setExtras] = useState({ summary: null, progress: null });
  const [plan, setPlan] = useState(null);
  const [pod, setPod] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // {text, bad}
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState(null);
  const [note, setNote] = useState(null);
  const [labelsOffer, setLabelsOffer] = useState(null);
  const [docOffer, setDocOffer] = useState(null);
  const [finalizeNote, setFinalizeNote] = useState(null);
  const [podOpen, setPodOpen] = useState(false);
  // Load truck for a delivery order's pallets ({palletIds}), with delivery signing on (null = closed)
  const [loadTruck, setLoadTruck] = useState(null);
  const signing = useShippingSigningFlags();
  // Open problems from the pallet check screen ({} when that call fails).
  const palletChecks = usePalletChecks(plan ? plan.plan_id : null);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const fresh = () => seq === loadSeq.current;
    try {
      const truth = await loadOrderTruth(soId);
      if (!fresh()) return;
      setDetail(truth);
      setError(null);
      const ex = await loadOrderExtras(soId);
      if (!fresh()) return;
      setExtras(ex);
      setPlan(ex.plan);
    } catch (err) {
      if (fresh()) setError(err.message || "Couldn't load the order.");
    }
  }, [soId]);

  useEffect(() => {
    load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  const reloadPlan = useCallback(async () => {
    try { setPlan(await loadOrderPlan(soId)); } catch (_) { /* the pallets stay as shown */ }
  }, [soId]);

  const planId = plan?.plan_id;
  const loadPodRecords = useCallback(async () => {
    if (!planId) { setPod(null); return; }
    try { setPod(await loadPod(planId)); } catch (_) { setPod(null); }
  }, [planId]);
  useEffect(() => { loadPodRecords(); }, [loadPodRecords]);

  const header = detail?.header;
  const flat = useMemo(() => (detail ? flattenTruthEntries(detail.lines) : []), [detail]);
  // Manifest pieces count for the order whose paper they are on (as the desktop: the order's own
  // id from its header). Without an id every paper would count by the job's share.
  const shownSoId = detail?.header?.sales_order_id ?? soId ?? null;
  const { lineStepsMap, shopDay } = useLineSteps(detail?.lines, detail?.production_data, shownSoId);
  // Production: this order's parts only, the job's other orders' parts behind a toggle (as the desktop).
  const scope = useMemo(() => orderScope(detail), [detail]);
  const [showOtherParts, setShowOtherParts] = useState({});
  useEffect(() => { setShowOtherParts({}); }, [soId]);
  // Stable between renders: the photo sheet keeps its state while records load under it.
  const eligible = useMemo(() => podEligiblePallets(plan?.pallets), [plan]);

  // --- Pallet actions: the shared module, with this screen's own state ---------------------
  const io = (key) => ({
    ui: phoneUi,
    setBusy: (on) => setBusy(on ? key : null),
    // The action clears the line as it starts; its result sets it (below), red when it failed.
    setMessage: (text) => { if (!text) setMessage(null); },
    reloadPlan,
  });

  const onAction = async (key, args) => {
    const pallet = args.pallet;
    const pid = pallet?.pallet_id;
    let r = null;
    if (key === "sealBox") {
      r = await sealBox({ plan, palletId: args.box.pallet_id, boxId: args.box.box_id, boxLabel: args.label, ...io(`sealBox_${args.box.box_id}`) });
    } else if (key === "reprintBoxLabel") {
      const owner = (plan.pallets || []).find((p) => (p.boxes || []).some((b) => b.box_id === args.box.box_id));
      r = await reprintBoxLabel({ shipmentGroupId: owner?.shipment_group_id, shipmentBoxId: args.box.shipment_box_id, boxLabel: args.label, ...io(`reprintBox_${args.box.box_id}`) });
    } else if (key === "seal") {
      r = await sealPallet({ plan, palletId: pid, ...io(`seal_${pid}`) });
    } else if (key === "ship") {
      // a delivery order never leaves with nothing to sign: Load truck (its code on the printed manifest)
      if (deliveryLoadsOn(plan, signing)) { setMessage(null); setLoadTruck({ palletIds: [pid] }); return; }
      r = await shipPallet({ plan, palletId: pid, ...io(`ship_${pid}`) });
    } else if (key === "stage") {
      r = await stagePallet({ plan, palletId: pid, ...io(`stage_${pid}`) });
    } else if (key === "confirmPickup") {
      r = await confirmPickup({ plan, palletId: pid, ...io(`confirmPickup_${pid}`) });
    } else if (key === "reprintLabels") {
      r = await reprintPalletLabels({ shipmentGroupId: pallet.shipment_group_id, palletNum: pallet.pallet_num, palletName: args.name, ...io(`reprintLabels_${pid}`) });
    } else if (key === "statusLabel") {
      const { kind, pdfPath } = STATUS_LABELS.pallet(pid);
      r = await queueStatusLabel({ kind, idLabel: args.name, pdfPath, trigger: "phone_sales_order", ...io(`statusLabel_${pid}`) });
    }
    // Backed out (a "No" or a closed PIN sheet): leave what is on screen, offers included.
    if (!r || (!r.done && !r.message)) return;
    setLabelsOffer(r.labels || null);
    setDocOffer(r.document || null);
    setFinalizeNote(null);
    if (r.message) setMessage({ text: r.message, bad: !r.done });
    if (r.finalize) {
      setFinalizeNote(`That was the order's last pallet. Finalize ${header?.order_number || "the order"} (the customer's ${r.finalize.kind === "pickup" ? "Ready for Pickup" : "shipping"} email) from a computer: Sales Orders → this order → Packaging Plan.`);
      loadPodRecords();
    }
  };

  const printOffered = async () => {
    const offer = labelsOffer;
    setBusy("labels");
    try {
      const r = await printLabelsAtLaser({ path: offer.path, what: offer.what });
      setMessage({ text: r.message, bad: false });
      setLabelsOffer(null);
    } catch (err) {
      setMessage({ text: `Printing ${offer.what} failed: ${err.message || String(err)}`, bad: true });
    } finally {
      setBusy(null);
    }
  };

  // Load truck closed: a load went out -> re-read, and offer its manifest (driver + customer copy, the QR on it)
  const loadTruckClosed = (r) => {
    setLoadTruck(null);
    if (!r || !r.loaded) return;
    setLabelsOffer(null);
    setFinalizeNote(null);
    if (r.manifest) {
      setDocOffer({ url: absoluteUrl(API_BASE, r.manifest), filename: `Manifest_${plan?.order_number || "order"}.pdf`,
        what: "Manifest with the signing QR (driver + customer copy)" });
    }
    reloadPlan();
    loadPodRecords();
  };

  // Shipped pallets of a delivery order still waiting for a signature: show (or start) their ONE signing code
  const waitingCount = signing.delivery && plan?.fulfillment_type === "delivery" ? deliveryWaitingPallets(plan).length : 0;
  const deliveryQr = async () => {
    setMessage(null);
    const r = await startDeliveryQr({
      apiBase: API_BASE, ui: phoneUi, planId: plan.plan_id, orderNumber: plan.order_number || header?.order_number,
      customerName: plan.customer_name || header?.customer_name || null,
      setBusy: (on) => setBusy(on ? "delivery_qr" : null),
    });
    if (r.message) setMessage({ text: r.message, bad: !r.done });
    if (r.outcome) { reloadPlan(); loadPodRecords(); }
  };

  const stagedCount = (plan?.pallets || []).filter((p) => p.status === "Staged").length;
  const pickupAll = async () => {
    const r = await confirmPickupAll({ plan, stagedCount, ...io("confirmPickupAll") });
    if (!r.done && !r.message) return;
    setLabelsOffer(null); setDocOffer(null); setFinalizeNote(null);
    if (r.message) setMessage({ text: r.message, bad: !r.done });
  };

  if (error && !detail) {
    return (
      <div className="jq-screen" data-testid="phone-sales-order">
        <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All orders</button>
        <div className="jq-note jq-note-danger" role="alert">{error}</div>
      </div>
    );
  }
  if (!detail) {
    return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
  }

  const totals = shippedTotals(flat); // the desktop's % shipped
  const summary = extras.summary;
  const accrued = summary ? accruedFinancials(summary) : null;
  const bases = summary ? summaryBases({ summary, header }) : null;
  const invoices = detail.related_documents?.invoices || [];
  const batches = shipmentBatches(detail.related_documents?.shipments || []);

  return (
    <div className="jq-screen" data-testid="phone-sales-order">
      <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All orders</button>

      <section className="jq-card" data-testid="so-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">{header.order_number}</span>
          {isTm(header) && <span className="jq-tag jq-tag-warn">T&amp;M</span>}
          <span className="jq-tag">{header.status}</span>
        </div>
        <div className="fw-semibold">{header.customer_name}</div>
        <div className="jq-muted">{FULFILL[header.fulfillment_type] || header.fulfillment_type}</div>
        <dl className="jq-kv mt-2 mb-0">
          <dt>Customer PO</dt>
          <dd data-testid="so-po">{header.customer_po || "N/A"}<EditButton label="customer PO" onClick={() => setEdit("po")} /></dd>
          {header.customer_so && (<><dt>Customer SO</dt><dd>{header.customer_so}</dd></>)}
          <dt>Promised</dt>
          <dd data-testid="so-promised">{header.promised_date ? fmtDay(header.promised_date) : (header.lead_time || "Not set")}<EditButton label="promised date" onClick={() => setEdit("promised")} /></dd>
          <dt>Target (internal)</dt>
          <dd data-testid="so-target">{header.target_completion_date ? fmtDay(header.target_completion_date) : "Follows the promise"}<EditButton label="target date" onClick={() => setEdit("target")} /></dd>
          {extras.progress?.delivery_risk?.est_complete_date && (
            <>
              <dt>Est. complete</dt>
              <dd>
                {fmtDay(extras.progress.delivery_risk.est_complete_date)}
                {extras.progress.delivery_risk.risk_reason && <div className="jq-muted">{extras.progress.delivery_risk.risk_reason}</div>}
              </dd>
            </>
          )}
          {header.ship_to_address && (
            <>
              <dt>Ship to</dt>
              <dd>
                {[header.ship_to_address, header.ship_to_address_line2].filter(Boolean).join(", ")}
                {(header.ship_to_city || header.ship_to_state) && `, ${[header.ship_to_city, header.ship_to_state].filter(Boolean).join(", ")} ${header.ship_to_zip || ""}`}
                {header.ship_to_receiving_hours && <div className="jq-muted">Receiving hours: {header.ship_to_receiving_hours}</div>}
              </dd>
            </>
          )}
        </dl>
        {note && <div className="jq-note jq-note-info mt-2" data-testid="so-propagation">{note}</div>}
      </section>

      <section className="jq-card" data-testid="so-delivery">
        <h2 className="jq-section-title">Delivery</h2>
        <div className="fw-semibold">{totals.totalShipped} of {totals.totalOrdered} shipped ({totals.percentComplete}%)</div>
        <div className="progress mt-1" style={{ height: 8 }} aria-hidden="true">
          <div className="progress-bar bg-success" style={{ width: `${Math.min(100, totals.percentComplete)}%` }} />
        </div>
      </section>

      <h2 className="jq-section-title mt-3">Lines</h2>
      {Object.keys(lineStepsMap).length > 0 && <div className="jq-muted mb-2">Tap a step for its details.</div>}
      {(detail.lines || []).map((entry, gi) => {
        if (entry?.type === "assembly_group") {
          return (
            <div key={`asm-${entry.source_assembly_id}-${gi}`} className="jq-card" data-testid={`so-assembly-${entry.source_assembly_id}`}>
              <div className="fw-semibold">{entry.assembly_name}</div>
              <div className="jq-muted mb-1">Assembly · {entry.sets ?? entry.quantity_ordered} set(s)</div>
              {(entry.components || []).map((l) => <LineRow key={l.sales_order_line_id} line={l} steps={lineStepsMap[l.sales_order_line_id]} />)}
            </div>
          );
        }
        const line = entry?.type === "item" ? entry.data : entry;
        if (!line?.sales_order_line_id) return null;
        return (
          <div key={line.sales_order_line_id} className="jq-card">
            <LineRow line={line} steps={lineStepsMap[line.sales_order_line_id]} />
          </div>
        );
      })}

      {(detail.production_data || []).length > 0 && (
        <>
          <h2 className="jq-section-title mt-3">Production</h2>
          {detail.production_data.map((job) => {
            // The job's own view (every paper counts), as the desktop's Production Detail.
            const routing = jobRouting(job.tasks, job.decoupled_defects, { today: shopDay, shownSoId });
            const owned = routing.parts.map((p) => ({ ...p, owners: partOwnersElsewhere(p.partId, job, scope) }));
            const mine = owned.filter((p) => !p.owners);
            // Nothing on the job is this order's (should not happen): show it whole.
            const others = mine.length > 0 ? owned.filter((p) => p.owners) : [];
            const shownParts = others.length > 0 ? mine : owned.map((p) => ({ ...p, owners: null }));
            const otherOrders = [...new Set(others.flatMap((p) => p.owners))];
            const othersOpen = !!showOtherParts[job.job_id];
            return (
              <div key={job.job_id} className="jq-card" data-testid={`so-job-${job.job_id}`}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Link to={`/jobs/${job.job_id}`} className="fw-bold flex-grow-1">Job #{job.master_job_num ?? job.job_id}</Link>
                  <span className="jq-tag">{job.job_status}</span>
                </div>
                {job._source === "allocated" && job._source_so_number && <div className="jq-muted">From {job._source_so_number}</div>}
                {routing.shared.length > 0 && (
                  <>
                    {otherOrders.length > 0 && <div className="jq-muted">Sheet shared with {otherOrders.join(", ")}</div>}
                    <RoutingStrip steps={routing.shared} ariaLabel={`Shared sheet for job ${job.master_job_num}`} />
                  </>
                )}
                {shownParts.concat(othersOpen ? others : []).map((p, i) => (
                  <div key={`${p.partId}-${i}`} className="mt-1" style={p.owners ? { opacity: 0.75 } : undefined}>
                    <div className="jq-muted">{p.productId}{p.owners ? ` · for ${p.owners.join(", ")}` : ""}</div>
                    <RoutingStrip steps={p.steps} ariaLabel={`Routing for ${p.productId}`} />
                  </div>
                ))}
                {others.length > 0 && (
                  <button type="button" className="btn btn-link btn-sm p-0 mt-1 text-decoration-none"
                    onClick={() => setShowOtherParts((prev) => ({ ...prev, [job.job_id]: !prev[job.job_id] }))}
                    aria-expanded={othersOpen}>
                    {othersOpen ? "Hide" : "Show"} {others.length} other {others.length === 1 ? "part" : "parts"} ({otherOrders.join(", ")})
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}

      {summary && (
        <section className="jq-card" data-testid="so-money">
          <h2 className="jq-section-title">Money</h2>
          <dl className="jq-kv mb-0">
            <dt>Billable now</dt><dd>{money(summary.actual_realizable_revenue)}</dd>
            <dt>Spent so far</dt><dd>{money(accrued.actualTotalCost)}</dd>
            <dt>Profit so far</dt><dd>{money(accrued.actualProfit)} ({pct(accrued.actualMargin)})</dd>
            <dt>Est. profit</dt><dd>{money(bases.est_profit)}{bases.est_margin != null ? ` (${pct(bases.est_margin * 100)})` : ""}</dd>
          </dl>
        </section>
      )}

      {plan && (
        <>
          <h2 className="jq-section-title mt-3">Pallets <span className="jq-muted">{plan.status}</span></h2>
          {message && <div className={`jq-note ${message.bad ? "jq-note-danger" : "jq-note-info"} mb-2`} role="status" data-testid="so-message">{message.text}</div>}
          {labelsOffer && (
            <div className="jq-card" data-testid="so-labels-offer">
              <div className="fw-semibold mb-2">{labelsOffer.what}</div>
              <button type="button" className="btn btn-primary btn-lg w-100" disabled={busy === "labels"} onClick={printOffered}>
                <i className="fas fa-print me-2" aria-hidden="true" />Print at the Laser Zebra
              </button>
              <button type="button" className="btn btn-link w-100" onClick={() => setLabelsOffer(null)}>Not now (print it from a computer)</button>
            </div>
          )}
          {docOffer && <PdfButton url={docOffer.url} filename={docOffer.filename} label={docOffer.what} fetchNow testId="so-doc-offer" />}
          {finalizeNote && <div className="jq-note jq-note-info mb-2" data-testid="so-finalize-note">{finalizeNote}</div>}
          {waitingCount > 0 && (
            <button type="button" className="btn btn-outline-primary btn-lg w-100 mb-2" data-testid="so-delivery-qr" disabled={!!busy} onClick={deliveryQr}>
              {busy === "delivery_qr"
                ? <><span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Starting…</>
                : <><i className="fas fa-qrcode me-2" aria-hidden="true" />Delivery signing QR ({waitingCount} shipped)</>}
            </button>
          )}
          {plan.fulfillment_type === "pickup" && stagedCount > 1 && (
            <button type="button" className="btn btn-success btn-lg w-100 mb-2" disabled={!!busy} onClick={pickupAll}>
              Customer here for all {stagedCount} pallets: confirm pickup
            </button>
          )}
          {(plan.pallets || []).filter((p) => p.status !== "Consolidated").map((p) => (
            <PalletCard key={p.pallet_id} plan={plan} pallet={p} busy={busy} onAction={onAction} openProblems={openProblemsFor(palletChecks, p.pallet_id)} />
          ))}

          <section className="jq-card" data-testid="so-pod">
            <h2 className="jq-section-title">{plan.fulfillment_type === "pickup" ? "Proof of pickup" : "Proof of delivery"}</h2>
            {(pod?.events || []).length === 0 && <div className="jq-muted mb-2">No delivery photos yet.</div>}
            {(pod?.events || []).map((ev) => (
              <div key={ev.event_id} className="mb-2">
                <div>
                  <span className="fw-semibold">{POD_KIND_LABEL[ev.kind] || ev.kind}</span>
                  {ev.received_by ? ` · ${ev.received_by}` : ""}
                  <span className="jq-muted"> · {(ev.photos || []).filter((ph) => ph.status === "ready").length} photo(s)</span>
                </div>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {(ev.photos || []).filter((ph) => ph.status === "ready" && ph.thumb_url).slice(0, 6).map((ph) => (
                    <img key={ph.photo_id} src={ph.thumb_url} alt={ph.original_filename || "photo"} width={64} height={48} style={{ objectFit: "cover", borderRadius: 4 }} loading="lazy" />
                  ))}
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-primary w-100" disabled={eligible.length === 0} onClick={() => setPodOpen(true)}>
              <i className="fas fa-camera me-2" aria-hidden="true" />Add delivery photos
            </button>
            {eligible.length === 0 && <div className="jq-muted mt-1">Photos go on pallets that have shipped or are staged for pickup.</div>}
          </section>
        </>
      )}

      <section className="jq-card" data-testid="so-documents">
        <h2 className="jq-section-title">Documents</h2>
        <PdfButton url={`${API_BASE}${orderPdfPath(soId)}`} filename={`${header.order_number}.pdf`} label={`Sales order ${header.order_number}`} testId="so-pdf" />
        {invoices.map((inv) => (
          <PdfButton key={inv.invoice_id} url={`${API_BASE}${invoicePdfPath(inv.invoice_id)}`} filename={`Invoice_${inv.invoice_number}.pdf`} label={`Invoice #${inv.invoice_number} · ${money(inv.total)} · ${inv.status}`} testId={`so-invoice-${inv.invoice_id}`} />
        ))}
        {batches.map((b) => {
          const path = batchManifestPath(b, "so");
          return path ? (
            <PdfButton key={b.id} url={`${API_BASE}${path}`} filename={`Manifest_${header.order_number}_${b.id}.pdf`} label={`Shipment ${fmtShipDay(b.date)} manifest`} testId="so-shipment" />
          ) : null;
        })}
      </section>

      <OrderEditPanel
        kind={edit}
        header={header}
        soId={soId}
        onClose={() => setEdit(null)}
        onSaved={({ header: h, note: n }) => {
          if (h) setDetail((prev) => ({ ...prev, header: h }));
          // Saved, but the order couldn't be re-read: say so instead of showing the old value.
          setNote(h ? n : [n, "Saved. Reopen the order to see the change."].filter(Boolean).join(" "));
          setEdit(null);
        }}
      />
      {loadTruck && plan && (
        <LoadTruckModal apiBase={API_BASE} ui={phoneUi} order={loadTruckOrderFromPlan(plan, loadTruck.palletIds)}
          onClose={loadTruckClosed} />
      )}
      {plan && (
        <PodSheet
          open={podOpen}
          plan={plan}
          soId={soId}
          pallets={eligible}
          limits={pod?.limits}
          onClose={() => setPodOpen(false)}
          onSaved={() => { setPodOpen(false); loadPodRecords(); }}
        />
      )}
    </div>
  );
}
